import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Download, ZoomIn, ZoomOut } from "lucide-react";
import { AeraButton, Modal } from "@aera/ui";
import type { DataTableTheme } from "../types";
import { MissingImagePlaceholder } from "./MissingImagePlaceholder";
import { AeraImage } from "./DataTableImage";
import { cn } from "../utils/cn";

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

export interface ImageViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  caption?: string;
  themeOverrides?: Partial<DataTableTheme>;
}

function ImageViewerSkeleton() {
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4"
      aria-hidden
    >
      <div className="h-full w-full animate-pulse rounded-xl bg-aera-lightgray" />
    </div>
  );
}

function ImageViewerBody({
  url,
  caption,
  scale,
  onScaleChange,
  onZoomDelta,
  onLoadedChange,
}: {
  url: string;
  caption?: string;
  scale: number;
  onScaleChange: (next: number) => void;
  onZoomDelta: (delta: number) => void;
  onLoadedChange: (loaded: boolean) => void;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const showSkeleton = !imageLoaded && !imageFailed;
  const canPan = scale > MIN_ZOOM && imageLoaded && !imageFailed;

  useLayoutEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    if (img.complete) {
      if (img.naturalWidth > 0) setImageLoaded(true);
      else setImageFailed(true);
      return;
    }

    setImageLoaded(false);
    setImageFailed(false);
  }, [url]);

  useEffect(() => {
    setOffset({ x: 0, y: 0 });
  }, [url]);

  useEffect(() => {
    onLoadedChange(imageLoaded && !imageFailed);
  }, [imageLoaded, imageFailed, onLoadedChange]);

  useEffect(() => {
    if (scale === MIN_ZOOM) setOffset({ x: 0, y: 0 });
  }, [scale]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !imageLoaded || imageFailed) return;

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const delta = event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      onZoomDelta(delta);
    };

    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", onWheel);
  }, [imageFailed, imageLoaded, onZoomDelta]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!canPan) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setOffset({
      x: drag.originX + event.clientX - drag.startX,
      y: drag.originY + event.clientY - drag.startY,
    });
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div
      ref={viewportRef}
      className="relative h-[min(52dvh,420px)] w-full overflow-hidden md:h-[52dvh]"
    >
      {showSkeleton ? <ImageViewerSkeleton /> : null}

      {imageFailed ? (
        <div className="flex h-full w-full items-center justify-center py-0 md:py-14">
          <MissingImagePlaceholder iconClassName="h-10 w-10" />
        </div>
      ) : (
        <div
          className={cn(
            "flex h-full w-full touch-none items-center justify-center",
            canPan && "cursor-grab active:cursor-grabbing",
          )}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onDoubleClick={() => onScaleChange(MIN_ZOOM)}
        >
          <AeraImage
            ref={imgRef}
            native
            src={url}
            alt={caption ?? "Slip Image"}
            draggable={false}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageFailed(true)}
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            }}
            className={cn(
              "relative z-1 block h-full w-full origin-center object-contain transition-opacity duration-300",
              imageLoaded ? "opacity-100" : "opacity-0",
            )}
          />
        </div>
      )}
    </div>
  );
}

export function ImageViewer({
  open,
  onOpenChange,
  imageUrl,
  caption,
}: ImageViewerProps) {
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [downloadReady, setDownloadReady] = useState(false);
  const [scale, setScale] = useState(MIN_ZOOM);

  const clampZoom = useCallback(
    (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value)),
    [],
  );

  const applyZoom = useCallback(
    (next: number) => setScale(clampZoom(next)),
    [clampZoom],
  );

  const adjustZoom = useCallback(
    (delta: number) => setScale((prev) => clampZoom(prev + delta)),
    [clampZoom],
  );

  useEffect(() => {
    if (open) {
      setDisplayUrl(imageUrl);
      setDownloadReady(false);
      setScale(MIN_ZOOM);
    }
  }, [open, imageUrl]);

  const contentUrl = open ? imageUrl : displayUrl;

  return (
    <Modal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      onExited={() => {
        setDisplayUrl(null);
        setDownloadReady(false);
        setScale(MIN_ZOOM);
      }}
      title={caption ?? "สลิป"}
      size="3xl"
      zIndexClass="z-[10070]"
      bodyClassName="w-full p-2 sm:p-4"
      footer={
        contentUrl && downloadReady ? (
          <div className="flex w-full flex-wrap items-center justify-between gap-2 sm:gap-3">
            <div className="flex items-center gap-1 sm:gap-2">
              <AeraButton
                variant="secondary"
                fontWeight="medium"
                aria-label="ซูมออก"
                disabled={scale <= MIN_ZOOM}
                icon={<ZoomOut className="h-4 w-4" />}
                onClick={() => applyZoom(scale - ZOOM_STEP)}
              />
              <span className="min-w-11 text-center text-sm font-medium text-aera-charcoal tabular-nums">
                {Math.round(scale * 100)}%
              </span>
              <AeraButton
                variant="secondary"
                fontWeight="medium"
                aria-label="ซูมเข้า"
                disabled={scale >= MAX_ZOOM}
                icon={<ZoomIn className="h-4 w-4" />}
                onClick={() => applyZoom(scale + ZOOM_STEP)}
              />
            </div>
            <AeraButton
              variant="secondary"
              fontWeight="medium"
              icon={<Download className="h-4 w-4" />}
              onClick={() => window.open(contentUrl, "_blank")}
            >
              บันทึกรูปภาพ
            </AeraButton>
          </div>
        ) : undefined
      }
    >
      {contentUrl ? (
        <div className="w-full min-h-[min(52dvh,420px)] md:min-h-[52dvh]">
          <ImageViewerBody
            key={contentUrl}
            url={contentUrl}
            caption={caption}
            scale={scale}
            onScaleChange={applyZoom}
            onZoomDelta={adjustZoom}
            onLoadedChange={setDownloadReady}
          />
        </div>
      ) : (
        <div className="relative flex h-[min(52dvh,420px)] w-full items-center justify-center md:h-[52dvh]">
          <ImageViewerSkeleton />
        </div>
      )}
    </Modal>
  );
}
